import { useRef, useState } from "react";
import { PITCH_W, PITCH_H, ZONE_COLS, ZONE_ROWS, zoneFor, techAt, EVENT_MAP, TECH_AREAS, TECH_MARGIN } from "@/lib/events";

const LINE = { fill: "none", stroke: "rgba(255,255,255,0.85)", strokeWidth: 0.35 };
const zw = PITCH_W / ZONE_COLS;
const zh = PITCH_H / ZONE_ROWS.length;

const Markings = () => (
  <g>
    {Array.from({ length: 12 }).map((_, i) => (
      <rect key={i} x={i * (PITCH_W / 12)} y={0} width={PITCH_W / 12} height={PITCH_H} fill={i % 2 ? "#0B2A1E" : "#0E3324"} />
    ))}
    <rect x={0} y={0} width={PITCH_W} height={PITCH_H} {...LINE} />
    <line x1={52.5} y1={0} x2={52.5} y2={PITCH_H} {...LINE} />
    <circle cx={52.5} cy={34} r={9.15} {...LINE} />
    <circle cx={52.5} cy={34} r={0.45} fill="#fff" />
    <rect x={0} y={13.84} width={16.5} height={40.32} {...LINE} />
    <rect x={0} y={24.84} width={5.5} height={18.32} {...LINE} />
    <circle cx={11} cy={34} r={0.45} fill="#fff" />
    <path d="M16.5 26.69 A9.15 9.15 0 0 1 16.5 41.31" {...LINE} />
    <rect x={88.5} y={13.84} width={16.5} height={40.32} {...LINE} />
    <rect x={99.5} y={24.84} width={5.5} height={18.32} {...LINE} />
    <circle cx={94} cy={34} r={0.45} fill="#fff" />
    <path d="M88.5 26.69 A9.15 9.15 0 0 0 88.5 41.31" {...LINE} />
    <rect x={-2} y={30.34} width={2} height={7.32} {...LINE} />
    <rect x={105} y={30.34} width={2} height={7.32} {...LINE} />
    <path d="M1 0 A1 1 0 0 1 0 1" {...LINE} />
    <path d="M104 0 A1 1 0 0 0 105 1" {...LINE} />
    <path d="M0 67 A1 1 0 0 1 1 68" {...LINE} />
    <path d="M105 67 A1 1 0 0 0 104 68" {...LINE} />
  </g>
);

const Zones = ({ hover, active, tp, rt }) =>
  ZONE_ROWS.map((r, ri) =>
    Array.from({ length: ZONE_COLS }).map((_, ci) => {
      const id = `${r}${ci + 1}`;
      const on = hover === id || active === id;
      const [lx, ly] = tp(ci * zw + 3, (ri + 1) * zh - 1.2, ci * zw + 1.2, ri * zh + 3);
      return (
        <g key={id} pointerEvents="none">
          <rect {...rt(ci * zw, ri * zh, zw, zh)} fill={on ? "rgba(204,255,0,0.18)" : "transparent"}
            stroke="rgba(255,255,255,0.12)" strokeWidth={0.2} strokeDasharray="1 1" />
          <text x={lx} y={ly} fontSize={2.4} fill={on ? "#CCFF00" : "rgba(255,255,255,0.35)"}
            fontFamily="Barlow Condensed" fontWeight="700">{id}</text>
        </g>
      );
    })
  );

const TechAreas = ({ hover, active, rt, tp, vertical }) =>
  TECH_AREAS.map((a) => {
    const on = hover === a || active === a;
    const [cx, cy] = tp(a.x + a.w / 2, a.y + a.h / 2);
    return (
      <g key={a.side} data-testid={`tech-area-${a.side}`} pointerEvents="none">
        <rect {...rt(a.x, a.y, a.w, a.h)} fill={on ? "rgba(204,255,0,0.22)" : "rgba(255,255,255,0.04)"}
          stroke={on ? "#CCFF00" : "rgba(255,255,255,0.7)"} strokeWidth={0.3} strokeDasharray="1 0.6" />
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={2} fontFamily="Barlow Condensed" fontWeight="700"
          fill={on ? "#CCFF00" : "rgba(255,255,255,0.75)"} transform={vertical ? `rotate(-90 ${cx} ${cy})` : undefined}>
          ÁREA TEC
        </text>
      </g>
    );
  });

export const Pitch = ({ onPick, picked, markers = [], interactive = false, showZones = true, vertical = false, testId = "pitch" }) => {
  const ref = useRef(null);
  const [hover, setHover] = useState(null);
  const W = vertical ? PITCH_H + TECH_MARGIN : PITCH_W;
  const H = vertical ? PITCH_W : PITCH_H + TECH_MARGIN;
  const vx = vertical ? -3 - TECH_MARGIN : -3;
  // pitch coords -> view coords (optional alternative point for horizontal layout)
  const tp = (x, y, hx = x, hy = y) => (vertical ? [PITCH_H - y, x] : [hx, hy]);
  const rt = (x, y, w, h) => (vertical ? { x: PITCH_H - y - h, y: x, width: h, height: w } : { x, y, width: w, height: h });

  const toPitch = (evt) => {
    const svg = ref.current;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    const [x, y] = vertical ? [p.y, PITCH_H - p.x] : [p.x, p.y];
    return { x: Math.min(PITCH_W, Math.max(0, x)), y: Math.min(PITCH_H + TECH_MARGIN, Math.max(0, y)) };
  };

  const handleClick = (evt) => {
    if (!interactive) return;
    const { x, y } = toPitch(evt);
    const zone = zoneFor(x, y);
    if (zone) onPick({ x: +x.toFixed(1), y: +y.toFixed(1), zone });
  };
  const pk = picked && tp(picked.x, picked.y);

  return (
    <svg ref={ref} data-testid={testId} viewBox={`${vx} -3 ${W + 6} ${H + 6}`} className={`w-full h-auto block ${interactive ? "cursor-crosshair" : ""}`}
      onClick={handleClick}
      onMouseMove={(e) => { if (!interactive) return; const p = toPitch(e); setHover(techAt(p.x, p.y) || zoneFor(p.x, p.y)); }}
      onMouseLeave={() => setHover(null)}>
      <rect x={vx} y={-3} width={W + 6} height={H + 6} fill="#081C15" />
      <g transform={vertical ? `translate(${PITCH_H},0) rotate(90)` : undefined}><Markings /></g>
      <TechAreas hover={interactive ? hover : null} active={picked && techAt(picked.x, picked.y)} rt={rt} tp={tp} vertical={vertical} />
      {showZones && <Zones hover={interactive ? hover : null} active={picked?.zone} tp={tp} rt={rt} />}
      {markers.map((m) => {
        const [cx, cy] = tp(m.x, m.y);
        const t = EVENT_MAP[m.type];
        return m.n ? (
          <g key={m.id} data-testid={`marker-${m.n}`}>
            <circle cx={cx} cy={cy} r={2.2} fill={t?.color} stroke="#000" strokeWidth={0.3} />
            <text x={cx} y={cy + 0.75} textAnchor="middle" fontSize={m.n > 99 ? 1.6 : 2.1} fontWeight="700"
              fontFamily="Manrope, sans-serif" fill={t?.text}>{m.n}</text>
          </g>
        ) : (
          <circle key={m.id} cx={cx} cy={cy} r={1.3} fill={t?.color} stroke="#000" strokeWidth={0.3} />
        );
      })}
      {pk && (
        <g>
          <circle cx={pk[0]} cy={pk[1]} r={2.6} fill="none" stroke="#CCFF00" strokeWidth={0.5} />
          <circle cx={pk[0]} cy={pk[1]} r={1} fill="#CCFF00" />
        </g>
      )}
    </svg>
  );
};
